import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { Fragment, ReactNode } from 'react';
import { Field, FormSection } from 'redux-form';
import styled from 'styled-components';

import InputWithHoverEditField from 'dashboard/components/FormControls/InputWithHoverEditField';
import { Heading, Paragraph } from 'design-system/components/Text';
import { Hairline } from 'design-system/variables/effect';
import { UiStepType } from 'lib/interfaces/StepsConfig';

type PanelType = UiStepType.Complete | 'workflow' | 'screen';

const DEFAULT_PADDING = 24;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const HeaderContainer = styled.div<{ padding: number }>`
  padding: ${({ padding }) => padding}px;
  padding-top: ${Spacing[4]};
  box-shadow: ${Hairline.Bottom};
`;

const FieldWrapper = styled.div`
  width: 100%;
  & > div {
    min-height: auto;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${Spacing[1]};
`;

const HeaderButton = styled.button`
  background: none;
  border: 0;
`;

const StyledIconX = styled(XIcon).attrs(() => ({ size: 24 }))`
  cursor: pointer;
  min-width: ${Spacing[5]};
  margin-left: ${Spacing[4]};
`;

const BodyContainer = styled.div<{ padding: number }>`
  padding: ${({ padding }) => padding}px;
  padding-bottom: 0;
  flex: auto;

  & > div {
    margin-right: 0;
    margin-left: 0;
  }
`;

interface ConfigPanelProps {
  panelType: PanelType;
  // redux-form convenience
  name?: string;
  // needed when not all child components share the same prepended path
  fieldsBase?: string;
  title: string;
  description?: string;
  children?: ReactNode;
  headerPadding?: number;
  bodyPadding?: number;
  editable: boolean;
  // set to false if always present with default non-selected state
  closeable?: boolean;
  handleClose?: () => void;
}

const ConfigPanel = ({
  panelType,
  name,
  fieldsBase,
  title,
  description,
  children,
  editable,
  headerPadding = DEFAULT_PADDING,
  bodyPadding = DEFAULT_PADDING,
  closeable = true,
  handleClose,
}: ConfigPanelProps) => {
  // used for updating step/component names in redux-form
  // ITV steps/components use 'name-display', workflow steps use 'name'
  const nameField = panelType === 'workflow' ? 'name' : 'name-display';
  return (
    <FormSection name={name ?? ''} component={Fragment}>
      <Container id='ite-control-panel__container'>
        <HeaderContainer padding={headerPadding}>
          <HeadingContainer>
            <FieldWrapper>
              <Field
                type='text'
                placeholder={title}
                name={`${fieldsBase}.${nameField}`}
                component={InputWithHoverEditField}
                displayElement={<Heading size='sm' />}
                disabled={!editable}
                format={(value: string | undefined) =>
                  (value ?? '').replace('Conditional Step', 'Branching Step')
                }
                autoFocus
              />
            </FieldWrapper>
            {closeable && (
              <HeaderButton
                type='button'
                id='config-panel__close-button'
                data-cy='config-panel__close-button'
                onClick={handleClose}
              >
                <StyledIconX />
              </HeaderButton>
            )}
          </HeadingContainer>
          {description && <Paragraph>{description}</Paragraph>}
        </HeaderContainer>
        <BodyContainer padding={bodyPadding}>{children}</BodyContainer>
      </Container>
    </FormSection>
  );
};

export default ConfigPanel;
